var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert, Snackbar, Card, CardContent, Grid, useTheme, useMediaQuery, } from '@mui/material';
import { Add as AddIcon, GetApp as ExportIcon } from '@mui/icons-material';
import { machineService } from '@services/machineService';
import { formatDateForFilename } from '@utils/dateUtils';
import { businessService } from '@services/businessService';
import { useAuth } from '@contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MachineModal from './MachineModal';
const Machines = ({ hideTitle = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { isAdmin, user } = useAuth();
    const navigate = useNavigate();
    const [machines, setMachines] = useState([]);
    const [businesses, setBusinesses] = useState([]);
    const [selectedBusinessId, setSelectedBusinessId] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [machineToDelete, setMachineToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [adminToastShown, setAdminToastShown] = useState(false);
    useEffect(() => {
        loadBusinesses();
    }, []);
    useEffect(() => {
        // Show admin notification toast on mount
        if (isAdmin && !adminToastShown) {
            setSnackbar({
                open: true,
                message: 'You are currently an admin user and can see all businesses, machines, service histories and jobs for all users',
                severity: 'info',
            });
            setAdminToastShown(true);
        }
    }, [isAdmin, adminToastShown]);
    useEffect(() => {
        if (businesses.length > 0) {
            loadMachines();
        }
    }, [selectedBusinessId, businesses]);
    const loadBusinesses = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const data = yield businessService.getAll();
            setBusinesses(data);
            // Set to user's default business
            if (user === null || user === void 0 ? void 0 : user.defaultBusinessId) {
                setSelectedBusinessId(user.defaultBusinessId);
            }
            else if (data.length > 0) {
                setSelectedBusinessId(data[0].id);
            }
        }
        catch (error) {
            console.error('Error loading businesses:', error);
            showSnackbar('Error loading businesses', 'error');
        }
    });
    const loadMachines = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            let data;
            if (selectedBusinessId === '') {
                data = yield machineService.getAll();
            }
            else {
                data = yield machineService.getAll(selectedBusinessId);
            }
            setMachines(data);
        }
        catch (error) {
            console.error('Error loading machines:', error);
            showSnackbar('Error loading machines', 'error');
        }
        finally {
            setLoading(false);
        }
    });
    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };
    const handleCloseSnackbar = () => {
        setSnackbar(Object.assign(Object.assign({}, snackbar), { open: false }));
    };
    const handleOpenModal = (machine) => {
        setSelectedMachine(machine || null);
        setModalOpen(true);
    };
    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedMachine(null);
    };
    const handleSaveMachine = (data) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const machine = data;
            if (machine.id) {
                yield machineService.update(machine.id, machine);
                showSnackbar('Machine updated successfully', 'success');
            }
            else {
                // Add the selected business ID when creating a new machine
                const machineToCreate = Object.assign(Object.assign({}, machine), { businessId: selectedBusinessId });
                yield machineService.create(machineToCreate);
                showSnackbar('Machine added successfully', 'success');
            }
            handleCloseModal();
            loadMachines();
        }
        catch (error) {
            console.error('Error saving machine:', error);
            showSnackbar('Error saving machine', 'error');
        }
    });
    const handleOpenDeleteDialog = (machine) => {
        setMachineToDelete(machine);
        setDeleteDialogOpen(true);
    };
    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setMachineToDelete(null);
    };
    const handleDeleteMachine = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!machineToDelete)
            return;
        try {
            yield machineService.delete(machineToDelete.id);
            showSnackbar('Machine deleted successfully', 'success');
            handleCloseDeleteDialog();
            loadMachines();
        }
        catch (error) {
            console.error('Error deleting machine:', error);
            showSnackbar('Error deleting machine', 'error');
        }
    });
    const handleExportToExcel = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (machines.length === 0) {
            showSnackbar('No machines to export', 'error');
            return;
        }
        try {
            const businessName = ((_a = businesses.find(b => b.id === selectedBusinessId)) === null || _a === void 0 ? void 0 : _a.name) || 'All';
            const filename = `Machines_${businessName}_${formatDateForFilename()}.xlsx`;
            // Call API to get Excel file
            const blob = yield machineService.exportToExcel(selectedBusinessId);
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            showSnackbar('Machines exported successfully', 'success');
        }
        catch (error) {
            console.error('Error exporting machines:', error);
            showSnackbar('Error exporting machines', 'error');
        }
    });
    const handleCardClick = (machine) => {
        navigate(`/machines/${machine.id}`);
    };
    const columns = [];
    return (_jsxs(Box, { children: [!hideTitle && (_jsx(Typography, { variant: "h5", sx: { mb: 2 }, children: "Machines" })), _jsx(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }, children: _jsxs(Box, { sx: { display: 'flex', gap: 2 }, children: [_jsx(Button, { variant: "outlined", startIcon: _jsx(ExportIcon, {}), onClick: handleExportToExcel, disabled: machines.length === 0, children: "Export" }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: () => handleOpenModal(), disabled: businesses.length === 0 || !selectedBusinessId, children: "Add Machine" })] }) }), loading ? (_jsx(Typography, { children: "Loading..." })) : (_jsx(Grid, { container: true, spacing: 2, children: machines.map((machine) => (_jsx(Grid, { item: true, xs: 12, children: _jsx(Card, { sx: {
                            cursor: 'pointer',
                            backgroundColor: 'rgb(0, 0, 0)',
                            '&:hover': {
                                boxShadow: 6,
                                transform: 'translateY(-2px)',
                                transition: 'all 0.2s ease-in-out',
                            },
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 120,
                        }, onClick: () => handleCardClick(machine), children: _jsx(CardContent, { sx: { textAlign: 'center', width: '100%' }, children: _jsx(Typography, { variant: "h6", component: "div", sx: { color: 'rgb(206, 204, 204)' }, children: machine.name }) }) }) }, machine.id))) })), _jsx(MachineModal, { open: modalOpen, machine: selectedMachine, onClose: handleCloseModal, onSave: handleSaveMachine }), _jsxs(Dialog, { open: deleteDialogOpen, onClose: handleCloseDeleteDialog, children: [_jsx(DialogTitle, { children: "Confirm Delete" }), _jsx(DialogContent, { children: _jsxs(DialogContentText, { children: ["Are you sure you want to delete the machine \"", machineToDelete === null || machineToDelete === void 0 ? void 0 : machineToDelete.name, "\"? This action cannot be undone."] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: handleCloseDeleteDialog, children: "Cancel" }), _jsx(Button, { onClick: handleDeleteMachine, color: "error", variant: "contained", children: "Delete" })] })] }), _jsx(Snackbar, { open: snackbar.open, autoHideDuration: 6000, onClose: handleCloseSnackbar, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, children: _jsx(Alert, { onClose: handleCloseSnackbar, severity: snackbar.severity, sx: { width: '100%' }, children: snackbar.message }) })] }));
};
export default Machines;
